import React, { useCallback, useEffect, useRef, useState } from "react";

const THUMB_PADDING = 5;
const MIN_THUMB_WIDTH = 40;
const ARROW_STEP = 40;

type ThumbGeometry = {
    width: number;
    left: number;
    travel: number;
    scrollableRange: number;
};

function computeThumbGeometry(
    contentWidth: number,
    containerWidth: number,
    scrollLeft: number,
): ThumbGeometry {
    const scrollableRange = Math.max(0, contentWidth - containerWidth);
    const hasOverflow = containerWidth > 0 && contentWidth > containerWidth;

    if (!hasOverflow) {
        return { width: 0, left: THUMB_PADDING, travel: 0, scrollableRange };
    }

    const width = Math.max(
        MIN_THUMB_WIDTH,
        Math.round((containerWidth / contentWidth) * containerWidth),
    );
    const travel = Math.max(0, containerWidth - width - THUMB_PADDING * 2);
    const left =
        scrollableRange > 0
            ? THUMB_PADDING +
              Math.round((scrollLeft / scrollableRange) * travel)
            : THUMB_PADDING;

    return { width, left, travel, scrollableRange };
}

/**
 * Drives the custom sticky horizontal scrollbar: thumb geometry, track click,
 * thumb drag and keyboard control. Pure parent-side UI logic — it only needs the
 * measured widths, the current scroll offset and a `scrollTo` command.
 */
export default function useHorizontalScrollbar(
    contentWidth: number,
    containerWidth: number,
    scrollLeft: number,
    scrollTo: (x: number) => void,
) {
    const trackRef = useRef<HTMLDivElement>(null);
    const dragOriginRef = useRef<{
        pointerX: number;
        scrollLeft: number;
    } | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const thumb = computeThumbGeometry(
        contentWidth,
        containerWidth,
        scrollLeft,
    );
    const { width: thumbWidth, travel, scrollableRange } = thumb;

    const handleTrackClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (dragOriginRef.current) return;
            const track = trackRef.current;
            if (!track) return;
            const clickX =
                e.clientX - track.getBoundingClientRect().left - THUMB_PADDING;
            const ratio = Math.max(
                0,
                Math.min(1, (clickX - thumbWidth / 2) / travel),
            );
            scrollTo(ratio * scrollableRange);
        },
        [thumbWidth, travel, scrollableRange, scrollTo],
    );

    const handleThumbMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            dragOriginRef.current = { pointerX: e.clientX, scrollLeft };
            setIsDragging(true);
        },
        [scrollLeft],
    );

    // Track on window so the drag survives the cursor leaving the thumb (e.g. over the iframe).
    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (e: MouseEvent) => {
            const origin = dragOriginRef.current;
            if (!origin) return;
            const pointerDelta = e.clientX - origin.pointerX;
            const scrollDelta =
                travel > 0 ? (pointerDelta / travel) * scrollableRange : 0;
            scrollTo(origin.scrollLeft + scrollDelta);
        };

        const stopDragging = () => {
            dragOriginRef.current = null;
            setIsDragging(false);
        };

        window.addEventListener("mousemove", handlePointerMove);
        window.addEventListener("mouseup", stopDragging);

        return () => {
            window.removeEventListener("mousemove", handlePointerMove);
            window.removeEventListener("mouseup", stopDragging);
        };
    }, [isDragging, travel, scrollableRange, scrollTo]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "ArrowRight") {
                e.preventDefault();
                scrollTo(scrollLeft + ARROW_STEP);
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                scrollTo(scrollLeft - ARROW_STEP);
            } else if (e.key === "Home") {
                e.preventDefault();
                scrollTo(0);
            } else if (e.key === "End") {
                e.preventDefault();
                scrollTo(scrollableRange);
            }
        },
        [scrollLeft, scrollableRange, scrollTo],
    );

    return {
        trackRef,
        thumb,
        isDragging,
        handleTrackClick,
        handleThumbMouseDown,
        handleKeyDown,
    };
}
